import { Button, Divider } from '@aon/aon-tws-common-ui';
import * as React from 'react';
import { DistributionListContainer } from '../../containers/audience/distribution/DistributionListContainer';
import { PreviousStepButton, SaveButton, SaveButtonsGroup } from '../../containers/save-buttons/SaveButtonsGroup';
import ContentConstants from '../../contentConstants';
import { AudiencePropertiesUnderValidation, DistributionType, ExternalApplicationType } from '../../models';
import { ActionCreators, AudienceDetailsState } from '../../store/AudienceDetailsStore';
import { ActionCreators as DistributionContactActionCreators, IDistributionContactState } from '../../store/DistributionContactStore';
import { ActionCreators as DistributionFileActionCreators, IDistributionFileState } from '../../store/DistributionFileStore';
import { ActionCreators as EmailActionCreators, IEmailState } from '../../store/EmailStore';
import * as EmailTemplateActionCreators from '../../store/EmailTemplateSlice';
import { IEmailTemplateState } from '../../store/EmailTemplateSlice';
import { ISurveyParticipantState, ActionCreators as SurveyParticipantActionCreators } from '../../store/SurveyParticipantStore';
import { ValidationErrorHelper } from '../../utilities/ValidationErrorHelper';
import { AudienceMode } from '../audiences/AudienceMode';
import { CardLayout, Section, WarningModal } from '../common';
import { ErrorMessageLabel } from './SharedComponents';
import { DistributionNotificationRouter } from './distributionOptions/DistributionNotificationRouter';
import { DistributionOptionDataTransferModal } from './distributionOptions/DistributionOptionDataTransferModal';
import { DistributionTypeOptions } from './distributionOptions/DistributionTypeOptions';

interface IDistributionOptionsProps {
	externalApplicationType?: ExternalApplicationType;
	mode: AudienceMode;
	surveyId: number;
	surveySupportedCultures?: string[];
}

export type DistributionOptionsParams = IDistributionOptionsProps &
	typeof ActionCreators &
	AudienceDetailsState &
	typeof EmailActionCreators &
	IEmailState &
	typeof EmailTemplateActionCreators &
	IEmailTemplateState &
	typeof DistributionFileActionCreators &
	IDistributionFileState &
	typeof DistributionContactActionCreators &
	IDistributionContactState &
	typeof SurveyParticipantActionCreators &
	ISurveyParticipantState & {
		isOpen: boolean;
	};

export class DistributionOptions extends React.Component<
	DistributionOptionsParams,
	{
		validationErrorHelper: ValidationErrorHelper<AudiencePropertiesUnderValidation>;
	}
> {
	constructor(props: DistributionOptionsParams) {
		super(props);

		this.state = {
			validationErrorHelper: this.getValidationHelper(this.props),
		};

		this.closeUnsavedWarningModal = this.closeUnsavedWarningModal.bind(this);
		this.discardUnsavedChanges = this.discardUnsavedChanges.bind(this);
		this.discardUnsavedChangesAndGotoList = this.discardUnsavedChangesAndGotoList.bind(this);
		this.goPreviousStep = this.goPreviousStep.bind(this);
	}

	public UNSAFE_componentWillReceiveProps(props: DistributionOptionsParams) {
		if (this.props.isOpen !== props.isOpen && !props.isOpen) {
			this.props.GetDistributions(this.props.surveyId, this.props.audience.audienceId);
		}
		this.setState({
			validationErrorHelper: this.getValidationHelper(props),
		});
	}

	public render() {
		const {
			audience,
			distributionOptionDataTransferModalOpen,
			lastSavedAudience,
			loading,
			mode,
			surveyId,
			unsavedChangesWarningModalOpen,
		} = this.props;

		return (
			<CardLayout title={ContentConstants.DISTRIBUTIONOPTIONS_PAGE_TITLE} loading={loading}>
				<Section sectionHeader={ContentConstants.SECTIONHEADERS_DISTRIBUTION_OPTIONS}>
					<DistributionTypeOptions />

					<DistributionNotificationRouter />

					<DistributionListContainer surveyId={surveyId} audienceId={audience.audienceId} />

					{!this.state.validationErrorHelper.IsValid(AudiencePropertiesUnderValidation.DistributionType) && (
						<ErrorMessageLabel>
							{this.state.validationErrorHelper.Errors(AudiencePropertiesUnderValidation.DistributionType)}
						</ErrorMessageLabel>
					)}
				</Section>
				<Divider type='dashed' />
				<SaveButtonsGroup>
					<PreviousStepButton onClick={this.goPreviousStep} />
					<SaveButton
						disabled={this.props.saveInProgress || this.props.isUploading || this.props.isTransferDataFileValidating}
						onClick={() => this.props.Save(mode, false)}
					/>
					<Button
						id='SaveAndCompleteButton'
						color='primary'
						size='lg'
						disabled={this.props.saveInProgress || this.props.isUploading || this.props.isTransferDataFileValidating}
						onClick={() => this.props.Save(mode, true)}
					>
						{mode === AudienceMode.Edit ? ContentConstants.BUTTONS_SAVE_AND_COMPLETE : ContentConstants.BUTTONS_SAVE_AND_PUBLISH}
					</Button>
				</SaveButtonsGroup>
				{}

				{unsavedChangesWarningModalOpen && (
					<WarningModal
						isOpen={true}
						contentHeadline={ContentConstants.WARNING_MODAL_UNSAVED_HEADLINE}
						contentBody={ContentConstants.WARNING_MODAL_UNSAVED_BODY}
						onSubmit={this.discardUnsavedChangesAndGotoList}
						onClose={this.closeUnsavedWarningModal}
					/>
				)}

				{distributionOptionDataTransferModalOpen &&
					(!lastSavedAudience.distributionType ||
						(lastSavedAudience.distributionType &&
							this.props.pendingDistributionType === DistributionType.SingleLinkWithoutDistribution)) && (
						<WarningModal
							isOpen={true}
							contentHeadline={ContentConstants.WARNING_DISTRIBUTION_OPTION_WARNING_HEADLINE}
							contentBody={ContentConstants.WARNING_DISTRIBUTION_OPTION_WARNING_BODY}
							onSubmit={this.discardUnsavedChanges}
							onClose={this.closeUnsavedWarningModal}
						/>
					)}

				{distributionOptionDataTransferModalOpen &&
					lastSavedAudience.distributionType &&
					this.props.pendingDistributionType !== DistributionType.SingleLinkWithoutDistribution && (
						<DistributionOptionDataTransferModal
							modalOpen={lastSavedAudience.distributionType && distributionOptionDataTransferModalOpen}
							closeModal={() => this.props.ToggleDistributionOptionDataTransferModal(false)}
							onSubmit={(transfer: boolean) => this.props.HandleDistributionDataTransfer(this.props.pendingDistributionType!, transfer)}
						/>
					)}
			</CardLayout>
		);
	}

	public getValidationHelper(props: DistributionOptionsParams) {
		return new ValidationErrorHelper<AudiencePropertiesUnderValidation>(props.audience.validationErrors);
	}

	public goPreviousStep() {
		const { audience, distribution, fileUploadType, file, invitationEmail, reminderEmails, mode } = this.props;
		this.props.GoToPreviousStep(audience, mode, invitationEmail, reminderEmails, distribution, fileUploadType, file);
	}

	public discardUnsavedChangesAndGotoList() {
		const { audience, mode } = this.props;
		this.props.ToggleUnsavedWarningModal(false);
		this.props.GoToAudienceDetails(audience.surveyId, mode, audience.audienceId);
	}

	public discardUnsavedChanges() {
		const { pendingDistributionType } = this.props;
		this.props.ToggleUnsavedWarningModal(false);
		this.props.HandleDistributionDataTransfer(pendingDistributionType!, false);
	}

	public closeUnsavedWarningModal() {
		this.props.ToggleUnsavedWarningModal(false);
	}
}
